import { DEFAULT_GROUP_ID, LOGIN_UNIFICADO } from "@/configs/runtime";
import { DEFAULT_HEADER_GROUP_ID } from "@/configs/system";
import { estaDoisFatoresPendente } from "./estadoAutenticacao.space";
import {
	getGlobalHeadersSpaceApi,
	removeGlobalHeadersSpaceApi,
	setGlobalHeadersSpaceApi,
} from "./globalHeaders.spaceApi";

// ------------------------------- CONSTANTS -------------------------------
const CABECALHO_AUTORIZACAO = "Authorization";
const PREFIXO_TOKEN_BEARER = "Bearer ";
const CHAVE_GRUPO_LOGIN_UNIFICADO = "jwt_group_id";

// --------------------------------- TYPES ---------------------------------
type CallbackFalhaAutenticacaoSpaceApi = () => void;
type ExecutorRefreshTokenSpaceApi = () => Promise<string | null>;
type AplicarSessaoAutenticadaSpaceApiOptions = {
	readonly token: string;
	readonly groupId: string;
};

let callbackFalhaAutenticacao: CallbackFalhaAutenticacaoSpaceApi | null = null;
let refreshEmAndamento: Promise<string | null> | null = null;
let executarRefreshToken: ExecutorRefreshTokenSpaceApi | null = null;

// ------------------------------- FUNCTIONS -------------------------------
export function normalizarCabecalhoGrupoSpaceApi(value: unknown): string {
	if (typeof value !== "string" && typeof value !== "number") return "";

	const normalizado = String(value).trim();
	if (!normalizado) return "";

	const token = normalizado.toLowerCase();
	if (token === "undefined" || token === "null") return "";

	return normalizado;
}

function obterGrupoLoginUnificadoPersistido(): string {
	if (!LOGIN_UNIFICADO) return "";

	try {
		return normalizarCabecalhoGrupoSpaceApi(localStorage.getItem(CHAVE_GRUPO_LOGIN_UNIFICADO));
	} catch {
		return "";
	}
}

function persistirGrupoLoginUnificado(groupId: string): void {
	try {
		if (!LOGIN_UNIFICADO) {
			localStorage.removeItem(CHAVE_GRUPO_LOGIN_UNIFICADO);
			return;
		}

		const grupoNormalizado = normalizarCabecalhoGrupoSpaceApi(groupId);
		if (grupoNormalizado) localStorage.setItem(CHAVE_GRUPO_LOGIN_UNIFICADO, grupoNormalizado);
		else localStorage.removeItem(CHAVE_GRUPO_LOGIN_UNIFICADO);
	} catch {
		// ignore (storage can be unavailable)
	}
}

function removerGrupoLoginUnificadoPersistido(): void {
	try {
		localStorage.removeItem(CHAVE_GRUPO_LOGIN_UNIFICADO);
	} catch {
		// ignore (storage can be unavailable)
	}
}

export function definirExecutorRefreshTokenSpaceApi(executor: ExecutorRefreshTokenSpaceApi | null): void {
	executarRefreshToken = executor;
}

export function definirCallbackFalhaAutenticacaoSpaceApi(callback: CallbackFalhaAutenticacaoSpaceApi | null): void {
	callbackFalhaAutenticacao = callback;
}

export function notificarFalhaAutenticacaoSpaceApi(): void {
	callbackFalhaAutenticacao?.();
}

export function definirCabecalhosGlobaisAutenticacaoSpaceApi(cabecalhos: Record<string, string>): void {
	const proximo = { ...cabecalhos };
	if (DEFAULT_HEADER_GROUP_ID in proximo) {
		const groupId = normalizarCabecalhoGrupoSpaceApi(proximo[DEFAULT_HEADER_GROUP_ID]);
		if (groupId) proximo[DEFAULT_HEADER_GROUP_ID] = groupId;
		else {
			delete proximo[DEFAULT_HEADER_GROUP_ID];
			removeGlobalHeadersSpaceApi([DEFAULT_HEADER_GROUP_ID]);
		}
	}

	setGlobalHeadersSpaceApi(proximo);
}

export function removerCabecalhosGlobaisAutenticacaoSpaceApi(chavesCabecalho: string[]): void {
	removeGlobalHeadersSpaceApi(chavesCabecalho);
}

export function getCabecalhosGlobaisAutenticacaoSpaceApi(): Record<string, string> {
	return getGlobalHeadersSpaceApi();
}

export function getGrupoAtualAutenticacaoSpaceApi(): string {
	const cabecalhosGlobais = getGlobalHeadersSpaceApi();
	return (
		normalizarCabecalhoGrupoSpaceApi(cabecalhosGlobais[DEFAULT_HEADER_GROUP_ID]) ||
		obterGrupoLoginUnificadoPersistido() ||
		normalizarCabecalhoGrupoSpaceApi(DEFAULT_GROUP_ID)
	);
}

export function aplicarSessaoAutenticadaSpaceApi({
	token,
	groupId,
}: Readonly<AplicarSessaoAutenticadaSpaceApiOptions>): void {
	definirCabecalhosGlobaisAutenticacaoSpaceApi({
		[CABECALHO_AUTORIZACAO]: `${PREFIXO_TOKEN_BEARER}${token}`,
		[DEFAULT_HEADER_GROUP_ID]: groupId,
	});
	persistirGrupoLoginUnificado(groupId);
}

export function limparSessaoAutenticadaSpaceApi(): void {
	removerCabecalhosGlobaisAutenticacaoSpaceApi([CABECALHO_AUTORIZACAO, DEFAULT_HEADER_GROUP_ID]);
	removerGrupoLoginUnificadoPersistido();
}

// Retorna o token JWT atual ou null se não estiver presente ou for inválido
export function getTokenJwtSpaceApi(): string | null {
	const authorization = getGlobalHeadersSpaceApi()[CABECALHO_AUTORIZACAO] ?? "";
	if (!authorization.startsWith(PREFIXO_TOKEN_BEARER)) return null;

	return authorization.slice(PREFIXO_TOKEN_BEARER.length).trim() || null;
}

export async function atualizarTokenUmaVezSpaceApi(): Promise<string | null> {
	if (!executarRefreshToken) return null;

	refreshEmAndamento ??= executarRefreshToken().finally(() => {
		refreshEmAndamento = null;
	});

	return refreshEmAndamento;
}

export function aplicarTokenRotacionadoSpaceApi(token: string): void {
	if (estaDoisFatoresPendente()) return;

	definirCabecalhosGlobaisAutenticacaoSpaceApi({
		[CABECALHO_AUTORIZACAO]: `${PREFIXO_TOKEN_BEARER}${token}`,
	});
}
